import React, { createContext, useContext, useState, useEffect } from 'react';
import { useLazyGetFeatureListQuery } from '../store/apiSlice';

const ProductConfigContext = createContext();

const productFeatures = ["secondary_geofence","map","overspeed","reports","device_mgmt","intuparcel","white_label"]

export const useProductConfig = () => {
  const context = useContext(ProductConfigContext);
  if (!context) {
    throw new Error('useProductConfig must be used within a ProductConfigProvider');
  }
  return context;
};

const isPlainObject = (value) =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

const deepMerge = (target = {}, source = {}) => {
  const result = { ...target };

  Object.keys(source).forEach((key) => {
    const sourceValue = source[key];
    const targetValue = result[key];

    if (isPlainObject(sourceValue) && isPlainObject(targetValue)) {
      result[key] = deepMerge(targetValue, sourceValue);
    } else {
      result[key] = sourceValue;
    }
  });

  return result;
};

const checkUserConfig = (key, userConfig) => {
  let feature = { enabled: false, config_exists: false };
  
  if (key === 'secondary_geofence') {
    if (userConfig?.home?.otheroption?.hide?.secondary_geofence) {
      feature.enabled = false;
      feature.config_exists = true;
    }
  }
  
  return feature;
};

export const ProductConfigProvider = ({ children }) => {
  const [productConfig, setProductConfig] = useState(null);
  const [featureConfig, setFeatureConfig] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const [fetchFeatureList] = useLazyGetFeatureListQuery();

  const clearFeatureConfig = () => {
    setFeatureConfig({});
    setProductConfig(null);
  };

  const processFeatureConfig = (featureList, userConfig, isPublicPage = false) => {
    const tempFeature = {};
    const username = localStorage.getItem("user_details")?.username;

    productFeatures.forEach((feature) => {
      let temp = {};
      const configResponse = checkUserConfig(feature, userConfig);

      if (featureList?.length) {
        const features = featureList.filter(config => config?.feature === feature);
        
        if (features?.length) {
          const product = features.find(config => config?.hierarchy_level === "product");
          const clientFeature = features.find(config => config?.hierarchy_level === "client");
          const clientFeatures = features.filter(config => config?.hierarchy_level === "client" && config.enabled && config.trip_filters);
          const userFeature = features.find(config => 
            config?.hierarchy_level === "user" && 
            config?.users && 
            (Array.isArray(config?.users) 
              ? config?.users.includes(username) 
              : (typeof config?.users === "string" && config?.users === username))
          );

          temp.enabled = product?.enabled ? true : false;

          if (product?.enabled) {
            if (product?.config) {
              temp.config = { ...product?.config };
            }
          }
            if (clientFeature?.config || (Array.isArray(clientFeatures) && clientFeatures.length)) {
              if(Array.isArray(clientFeatures) && clientFeatures.length){
                temp.config_list = clientFeatures
                temp.multiple = true;
                temp.enabled = true;
              }
              else if (clientFeature?.config) {
                temp.enabled = clientFeature?.enabled ? true : false;
                temp.config = temp?.enabled && clientFeature?.config ? clientFeature.config : temp.config;
              }

              if (clientFeature?.enabled && (!userFeature?.enabled || configResponse.config_exists)) {
                temp.enabled = !userFeature?.enabled && userFeature?.config ? false : configResponse.config_exists ? configResponse.show : temp.enabled;
              }
            } else if ((userFeature?.config && !userFeature?.enabled) || configResponse.config_exists) {
              if(userFeature?.config && !userFeature?.enabled)
                temp.enabled = false
              else if(configResponse.config_exists)
                temp.enabled = configResponse.enabled;
            }
          

          if (temp?.enabled && temp.config) {
            temp = { ...temp, ...temp.config };
            delete temp.config;
          } else {
            delete temp.config;
          }
        }
      }

      // User config (config.feature_config.<feature>) takes priority over feature list config
      const userFeatureOverride = userConfig?.feature_config?.[feature];
      tempFeature[feature] = userFeatureOverride
        ? deepMerge(temp, userFeatureOverride)
        : temp;
    });

    // Only store in localStorage if not public page
    if (!isPublicPage) {
      localStorage.setItem("feature_config", JSON.stringify(tempFeature));
    }
    return tempFeature;
  };

  const updateProductConfig = async (token, isPublicPage = false) => {
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await fetchFeatureList(token).unwrap();
      if (response?.result) {
        const userConfig = JSON.parse(localStorage.getItem("app_config") || "{}");
        const processedConfig = processFeatureConfig(response?.result?.feature_config, userConfig, isPublicPage);
        setFeatureConfig(processedConfig);
        setProductConfig(response.result);
        return response.result;
      }
      
      throw new Error('Failed to fetch feature list');
    } catch (err) {
      setError(err?.message || 'Failed to fetch feature list');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  // Load initial feature config from localStorage only if not in public page
  useEffect(() => {
    const savedFeatureConfig = localStorage.getItem("feature_config");
    if (savedFeatureConfig && !window.location.pathname.includes('/public-trip')) {
      setFeatureConfig(JSON.parse(savedFeatureConfig));
    }
  }, []);

  const value = {
    productConfig,
    featureConfig,
    isLoading,
    error,
    updateProductConfig,
    clearFeatureConfig
  };

  return (
    <ProductConfigContext.Provider value={value}>
      {children}
    </ProductConfigContext.Provider>
  );
}; 